import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'semantic-ui-react';
import dayjs from 'dayjs';
import FixedPriceChart from './FixedPriceChart';
import { StyledPriceContainer, StyledPriceWrapper } from './style';
import { capitalizeFirstLetter, formatNumber, getPrefixData, handleErrors } from '~/utils/helpers';
import { useAuth } from '~/routes/components/AuthProvider';
import { ReportingService } from '~/api/services/reporting';
import { useParams } from 'react-router-dom';

const FixedPrice: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false)
  const { prefix } = useParams()

  const prefixData = getPrefixData(prefix)

  const { systemData: { amountUnit, amountVolume, amountPrice } } = useAuth()
  const priceLabel = `${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit}`
  const [fixedPriceSettings, setFixedPriceSettings] = useState(null)
  const [fixedPriceData, setFixedPriceData] = useState(null)
  const [dataFiltered, setDataFiltered] = useState({
    month: '',
    farmerGroupId: '',
    farmerId: ''
  })
  const [chartDomain, setChartDomain] = useState({
    dataMin: 0,
    dataMax: 0,
    divider: 0
  })

  useEffect(() => {
    fetchFixedPriceData()
  }, [prefix])

  async function fetchFixedPriceData() {
    setIsBusy(true)
    try {
      const response = await ReportingService(prefix).getFixedPriceSettings()
      setFixedPriceSettings(response.data)
      setDataFiltered({
        ...dataFiltered,
        month: response.data.selectedMonth
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function fetchFilteredData() {
    setIsBusy(true)
    try {
      const response = await ReportingService(prefix).getFixedPriceData({ ...dataFiltered })
      const months = response.data?.tradingMonths
      const minPrice = Math.min(...months.map(item => parseFloat(item.price)))
      const maxPrice = Math.max(...months.map(item => parseFloat(item.price)))
      const minBase = Math.min(...months.map(item => parseFloat(item.basePrice)))
      const maxBase = Math.max(...months.map(item => parseFloat(item.basePrice)))
      const min = isNaN(minBase) ? minPrice : minPrice < minBase ? minPrice : minBase
      const max = isNaN(maxBase) ? maxPrice : maxPrice > maxBase ? maxPrice : maxBase
      const divider = Math.pow(10, Math.floor(max).toString().length - 2)
      setChartDomain({
        dataMin: Math.floor(min / divider) * divider,
        dataMax: Math.floor(max / divider) * divider,
        divider
      })
      setFixedPriceData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered])

  function renderFilters() {
    return <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
      <Dropdown
        selection
        onChange={(e, data) => setDataFiltered({
          ...dataFiltered,
          month: data.value as string
        })}
        label={t('Trading month')}
        placeholder={t('Trading month')}
        selectOnBlur={false}
        value={dataFiltered.month || fixedPriceSettings?.selectedMonth}
        options={fixedPriceSettings?.months?.map(month => ({ value: month, text: capitalizeFirstLetter(dayjs(month).format('MMM YYYY')) }))}
      />
    </div>
  }

  function renderChart() {
    return <>
      {dataFiltered.month !== '' && <FixedPriceChart
        fixedPriceData={fixedPriceData}
        basePrice={fixedPriceSettings?.text?.translation}
        chartDomain={chartDomain}
        prefixData={prefixData}
      />
      }
    </>
  }

  function renderBaseDataInfo() {
    const price = capitalizeFirstLetter(prefixData.priceTitle)
    return <div style={{ marginBottom: 50 }}>
      <StyledPriceWrapper>
        <StyledPriceContainer>{t('Min {{price}}: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.minPrice ? formatNumber(fixedPriceData?.baseData?.minPrice) : 0, priceLabel, price })}
        </StyledPriceContainer>
        <StyledPriceContainer>{t('Max {{price}}: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.minPrice ? formatNumber(fixedPriceData?.baseData?.maxPrice) : 0, priceLabel, price })}
        </StyledPriceContainer>
      </StyledPriceWrapper>
      <StyledPriceWrapper>
        <StyledPriceContainer>{t('Avg {{price}}: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.avgPrice ? formatNumber(fixedPriceData?.baseData?.avgPrice) : 0, priceLabel, price })}
        </StyledPriceContainer>
        <StyledPriceContainer>{t('My avg. {{price}}: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.farmerAvgPrice ? formatNumber(fixedPriceData?.baseData?.farmerAvgPrice) : 0, priceLabel, price })}
        </StyledPriceContainer>
      </StyledPriceWrapper>
    </div>
  }

  return <Layout
    segmented
    isBusy={isBusy}
    filters={fixedPriceSettings && renderFilters()}
    title={prefixData.title}
  >
    {fixedPriceData?.baseData && renderBaseDataInfo()}
    {fixedPriceData && fixedPriceSettings && renderChart()}
  </Layout>;
};

export default FixedPrice;
